import { Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, interval, Observable, startWith, Subject, switchMap, tap } from "rxjs";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { AsyncPipe } from "@angular/common";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { HRowComponent } from "./h-row/h-row.component";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { AlertComponent } from "../../../../components/alert/alert.component";
import { genericSort } from "../../../../../../../shared/src/lib/utils/market";
import { CoreService } from "../../../../../../../shared/src/lib/services/core.service";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { TranslateModule } from "@ngx-translate/core";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { HighlightLoaderComponent } from "./highlight-loader/highlight-loader.component";
import { environment } from "../../../../../../../shared/src/environments/environment";
import { MarketOpsService } from "../../market-layout/market/market-ops.service";
import { map, takeUntil } from "rxjs/operators";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { Competition } from "../../../../../../../shared/src/lib/types/market.type";

@Component({
  selector: "app-sport-highlights",
  imports: [MatProgressBarModule, HRowComponent, AlertComponent, TranslateModule, HighlightLoaderComponent, AsyncPipe, MatIcon, MatTooltip],
  templateUrl: "./sport-highlights.component.html",
  styleUrl: "./sport-highlights.component.scss",
})
export class SportHighlightsComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  sportIds: Record<string, string> = {
    soccer: "1",
    cricket: "4",
    tennis: "2",
  };
  tHeaders: string[] = [];
  pageTitle = "";
  titles: Record<string, string> = {
    soccer: "Soccer",
    cricket: "Cricket",
    tennis: "Tennis",
  };
  markets = signal<any[]>([]);
  marketItems: string[] = [];
  currentSport: any = "";
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  coreService = inject(CoreService);
  placementService = inject(BetPlacementService);
  $loading!: Observable<boolean>;
  loader = inject(LoaderService);
  inPlayMarkets: string[] = [];
  marketOps = inject(MarketOpsService);
  parents: any[] = []; // max no of parents
  betPlacementService = inject(BetPlacementService);
  currentSort: { field: string; order: "asc" | "desc" } | null = null;
  isLoadingStatus = signal(false);
  router = inject(Router);
  route = inject(ActivatedRoute);
  leagues: Competition[] = [];
  selectedLeague: string = "";
  marketBackup: any[] = [];
  readonly marketsLength = computed(() => this.markets().length);

  ngOnInit() {
    this.$loading = combineLatest([this.loader.isLoading("/bf/match-odds-markets"), this.loader.isLoading("/v1/api/navigation_list")]).pipe(
      map(([loadingMarkets, loadingNavigationList]) => loadingMarkets || loadingNavigationList),
    );

    this.route.params
      .pipe(
        switchMap((params) => {
          // console.log(params["sport"]);
          this.pageTitle = "...";
          this.currentSport = params["sport"];

          return interval(120000).pipe(
            startWith(0),
            switchMap(() => this.getMatchOdds(params["sport"])),
          );
        }),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  getMatchOdds(sport: string): Observable<any> {
    this.isLoadingStatus.set(true);
    const params = {
      eventTypeId: this.sportIds[sport],
      maxResults: environment?.maxHighlightResults,
    };

    return this.marketService.fetchMatchOdds(params).pipe(
      tap({
        next: (response: any) => {
          if (response?.data && Array.isArray(response?.data)) {
            this.inPlayMarkets = [];
            this.marketItems = response?.data?.map((market: any) => `bf_${market?.marketId}`);
            const params = { market_ids: this.marketItems?.join(",") };
            // this.getSnapshot(params);
            this.getMarketStatus(params, response?.data);
            this.marketOps.setTopics(this.marketItems);
          } else {
            this.setFallback();
          }
        },
        error: (error: any) => {
          console.error(error);
        },
      }),
    );
  }

  getMarketStatus(params: any, marketItems: any[]) {
    const qParams = { ...params, statuses: "2,3", return_snapshot: "Y" };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const { snapshot: snapshots = {}, statuses = {} } = response || {};
          // console.log(snapshots);
          // console.log(statuses);
          if (marketItems && Array.isArray(marketItems)) {
            const openedMarkets = statuses["2"] || [];
            const suspendedMarkets = statuses["3"] || [];
            let _markets = marketItems
              .filter((market: any) => {
                const _marketId = `bf_${market?.marketId}`;
                return openedMarkets.includes(_marketId) || suspendedMarkets.includes(_marketId);
              })
              .map((market: any) => {
                const _marketId = `bf_${market?.marketId}`;
                return {
                  ...market,
                  mc: snapshots[_marketId],
                  eventName: market.event.name,
                };
              });
            // console.log(_markets);
            this.betPlacementService.currentPlacementConcernsMe(_markets, "marketId");
            this.setMarkets(_markets);
          } else {
            this.setFallback();
          }
        },
        error: (error) => {
          console.error("Failed to fetch market statuses:", error);
        },
      });
  }

  setMarkets(marketData: any[]) {
    if (!marketData) return;

    const hasDraw = marketData.some((item) => item?.runners?.length > 2);

    this.tHeaders = hasDraw ? ["1", "Draw", "2"] : ["1", "2"];

    const markets = marketData.map((item) => {
      const runners = item?.runners || [];

      if (hasDraw && runners.length === 2) {
        return {
          ...item,
          runners: [...runners, { runnerName: "The Draw" }],
        };
      }

      return item;
    });

    this.handleSort(markets, this.currentSort ? [this.currentSort.field] : ["marketStartTime", "eventName"], this.currentSort?.order);
    this.setLeagues(markets);
    this.selectedLeague = "";
    this.marketBackup = markets;
  }

  setLeagues(markets: any[]) {
    const competitionMap = new Map<string, Competition>();

    for (const market of markets) {
      const comp = market.competition;
      if (comp && !competitionMap.has(comp.id)) {
        competitionMap.set(comp.id, comp);
      }
    }

    const uniqueCompetitions = Array.from(competitionMap.values());

    this.leagues = [{ id: "", name: "All Leagues" }, ...uniqueCompetitions];
  }

  onSort(field: string) {
    if (!this.currentSort || this.currentSort.field !== field) {
      this.currentSort = { field, order: "asc" };
    } else if (this.currentSort.order === "asc") {
      this.currentSort.order = "desc";
    } else {
      this.currentSort = null;
    }

    const sortFields = this.currentSort ? [this.currentSort.field] : ["marketStartTime", "eventName"];

    this.handleSort(this.markets(), sortFields, this.currentSort?.order);
  }

  handleSort(markets: any[], sortFields: string[], sortOrder?: "asc" | "desc") {
    const _markets = genericSort(markets, sortFields, sortOrder);
    this.markets.set(_markets);
    this.pageTitle = `${this.titles[this.currentSport]} Highlights`;
    this.isLoadingStatus.set(false);
  }

  handleLeagueFilter(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    this.selectedLeague = selectedValue;
    console.log(this.marketBackup);
    const filteredMarkets = selectedValue ? this.marketBackup.filter((x) => x?.competition?.id === selectedValue) : this.marketBackup;
    this.markets.set(filteredMarkets);
  }

  setFallback() {
    this.isLoadingStatus.set(false);
    this.markets.set([]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.marketOps.setTopics([]);
  }
}
